// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA PANTALLA DEL PIN: DESBLOQUEAR, CAMBIAR DE USUARIO Y PONERSE EL PIN   (t95, 5-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL ENCARGO (el dueño): «a los 5 minutos de inactividad les "saque" a una pantalla con teclado numérico y tengan que
//  poner su PIN como en control horario», y «cambio de usuario por inactividad y desde mi perfil».
//
//  LOS MODOS, una sola pantalla a toda la pantalla (como el quiosco de fichar):
//   · 'desbloquear' · t136 (7-oct-2026) · SE ESCRIBE EL PIN Y TE RECONOCE, COMO EL QUIOSCO (el dueño: «se pone el PIN y te
//                     detecta, sin elegir antes quién eres»). POST /seguridad/pin/entrar: si es el PIN de la persona de la
//                     sesión, sigue donde estaba; si es el de OTRA persona de la clínica, el servidor abre una sesión NUEVA
//                     suya y cierra la de antes, y quien monta esto entra con ella como en un inicio de sesión y RECARGA la
//                     aplicación (nada de antes); si no es de nadie, «PIN incorrecto» (sin decir de quién no es). El freno
//                     de intentos y las puertas, en backend/services/cambioDeUsuario.js · entrarConPin. `cambio` (abierto
//                     con «Cambiar de usuario» o en un puesto compartido) solo cambia los rótulos: es el mismo teclado.
//   · 'elegir'      · solo para quien TODAVÍA NO TIENE PIN: su nombre (GET /seguridad/cambio-usuario/personas) y su
//                     contraseña (modo 'clave'). Quien tiene PIN no tiene que buscarse: lo escribe en el teclado.
//   · 'crear'       · ponerse el PIN de 6 cifras: un FORMULARIO (PIN nuevo, repetirlo y la contraseña, si no se acaba de
//                     escribir) y su botón «Guardar mi PIN» (PUT /seguridad/pin). t217 (13-oct-2026, B-1637) · el dueño: «si
//                     desbloqueas la pantalla omitiendo el pin, crea uno aleatorio sin más... le ha pasado a un usuario».
//                     Antes era el MISMO teclado que el de desbloquear y se guardaba solo en cuanto se escribían dos veces las
//                     mismas seis cifras, también con el teclado del ordenador: quien acababa de entrar con su contraseña y
//                     creía que seguía bloqueada (y la volvía a escribir) se quedaba con un PIN que no sabía. Ahora no hay
//                     teclado ni se escucha el del ordenador: solo se guarda con el botón, y «Ahora no» sale SIEMPRE.
//   · 'clave'       · quien ha tocado su nombre y NO tiene PIN («Sin PIN: entra con tu contraseña»): su correo ya puesto y su
//                     contraseña, por el inicio de sesión de siempre (POST /auth/login, con su freno de intentos); al
//                     entrar, la sesión de antes se cierra (POST /seguridad/cambio-usuario/cerrar-anterior, con su llave).
//
//  NADIE SE QUEDA ATRAPADO EN EL TECLADO (el dueño, 5-oct-2026): «Entrar con mi contraseña» y «Cerrar sesión» salen
//  SIEMPRE, y en «¿Quién eres?», «Entrar con otra cuenta» (al inicio de sesión de siempre) para quien no sale en la lista.
//
//  POR QUÉ ES UN `utils/` SIN MÁS IMPORT QUE REACT: lo monta components/BloqueoPantalla, y las guardias que montan
//  BloqueoPantalla cargan los `utils/` DE VERDAD (test-helpers/utilDelFrontend). Todo lo de fuera —la API, las
//  acciones— llega por props. Y TODOS sus hooks están en el componente de arriba: las piezas de dentro (teclas, puntos,
//  caras) no tienen estado, así que no se recrean ni pierden un toque al repintar el reloj (el fallo del quiosco).
//
//  NUNCA se escribe el PIN en ningún sitio que no sea la petición: ni en el almacenamiento del navegador, ni en un aviso.
import { useState, useEffect, useRef, useCallback } from 'react'
import { primeraMayuscula } from './primeraMayuscula'   // B-1913: «Viernes, 25 de septiembre», no «25 De Septiembre»
import { t as i18nT, tx, marca, tDato, localeFechas } from './i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const EVENTO_CAMBIAR_USUARIO = 'biango:cambiar-usuario'
export const LARGO_PIN = 6

/** Abre la pantalla de «Cambiar de usuario» desde cualquier sitio (el menú del perfil, Mi perfil). */
export function pedirCambioDeUsuario() {
  try { window.dispatchEvent(new CustomEvent(EVENTO_CAMBIAR_USUARIO)) } catch (_) { /* sin ventana: nada que abrir */ }
}

// ── LA LLAVE DE ESTE DISPOSITIVO ──────────────────────────────────────────────────────────────
// El dueño (5-oct): «que cada ordenador o dispositivo tenga una configuración diferente». Cada navegador se inventa una
// llave al azar la primera vez y la guarda; va en la cabecera X-Dispositivo de lo que pregunta por el bloqueo, y la clínica
// le pone nombre y regla en Seguridad de acceso («Este ordenador»). NO es de la sesión: sobrevive a salir y entrar (no está
// en la lista de limpieza de services/sesion), porque es del aparato. Y no abre nada: solo decide cuándo se tapa la
// pantalla. Si el navegador no deja guardar (modo privado), no hay llave y se aplica lo general.
export const CLAVE_DISPOSITIVO = 'biango_dispositivo'
export function llaveDeEsteDispositivo() {
  try {
    const ls = typeof localStorage !== 'undefined' ? localStorage : null
    if (!ls) return null
    const ya = ls.getItem(CLAVE_DISPOSITIVO)
    if (ya && /^[A-Za-z0-9_-]{32,64}$/.test(ya)) return ya
    const c = typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto : null
    if (!c) return null
    const b = new Uint8Array(32)
    c.getRandomValues(b)
    let s = ''
    for (const x of b) s += String.fromCharCode(x)
    const llave = btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    ls.setItem(CLAVE_DISPOSITIVO, llave)
    return ls.getItem(CLAVE_DISPOSITIVO) === llave ? llave : null
  } catch (_) { return null }
}
/** Las cabeceras con la llave de este dispositivo (para axios: `{ headers: cabeceraDispositivo() }`). */
export function cabeceraDispositivo() {
  const l = llaveDeEsteDispositivo()
  return l ? { 'X-Dispositivo': l } : {}
}

// Cuando alguien acaba de poner su PIN (Mi perfil), la pantalla de bloqueo vuelve a preguntar: deja de recordárselo.
export const EVENTO_PIN_PUESTO = 'biango:pin-puesto'
export function avisarPinPuesto() {
  try { window.dispatchEvent(new CustomEvent(EVENTO_PIN_PUESTO)) } catch (_) { /* sin ventana */ }
}

// ¿SALE «CAMBIAR DE USUARIO» EN EL MENÚ? Lo sabe components/BloqueoPantalla (el servidor manda `pin_unico` en
// GET /seguridad/bloqueo) y lo anuncia aquí; el menú del perfil lo LEE al pintarse. Sin hooks a propósito: el menú
// (components/Layout) lo montan guardias que recorren su árbol, y una pieza con estado propio no se puede expandir fuera
// de un pintado (lo mismo que utils/cambioDeClinica). Sale SIEMPRE en las cuentas de la clínica (lo pidió el dueño: «el
// botón en mi perfil de cambiar usuario»); si aquí no se puede pasar a otra persona (fuera de la red de la clínica), la
// pantalla lo dice. Sin la migración, o en una cuenta de Biango o del quiosco, no sale.
let _cambioDisponible = false
export function anunciarCambioDeUsuario(v) { _cambioDisponible = v === true }
export function hayCambioDeUsuario() { return _cambioDisponible }

/** «Cambiar de usuario» en el menú de la foto (`oscuro` = en la barra lateral). `onAntes` = cerrar el menú. */
export function CambioDeUsuario({ oscuro = false, onAntes }) {
  if (!_cambioDisponible) return null
  return (
    <button type="button" data-cambio-usuario={oscuro ? 'barra' : 'menu'}
      onClick={() => { if (onAntes) onAntes(); pedirCambioDeUsuario() }}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
        padding: oscuro ? '8px 10px' : '9px 12px', margin: oscuro ? '0 0 8px' : '0', borderRadius: '8px',
        fontSize: oscuro ? '12px' : '13px', fontWeight: 500,
        color: oscuro ? 'rgba(255,255,255,0.78)' : 'var(--text-primary)', background: oscuro ? 'rgba(255,255,255,0.04)' : 'transparent' }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="m17 8 3 3-3 3" /><path d="M20 11h-6" />
      </svg>
      <span style={{ flex: 1 }}>{i18nT('utils.pantallaDelPin.cambiar_de_usuario', 'Cambiar de usuario')}</span>
    </button>
  )
}

// ── LAS PIEZAS SIN ESTADO ──────────────────────────────────────────────────────────────────────
// Declaradas FUERA del componente: el tipo es siempre el mismo y React no desmonta los botones al repintar.
export function TeclaPin({ d, children, onPulsar, disabled, tono }) {
  const rojo = tono === 'rojo'
  return (
    <button type="button" data-tecla-pin={d} onClick={() => onPulsar(d)} disabled={disabled} className="pin-tecla"
      aria-label={d === '<' ? i18nT('utils.pantallaDelPin.borrar_la_ultima_cifra', 'Borrar la última cifra') : d === 'C' ? i18nT('utils.pantallaDelPin.borrar_todo', 'Borrar todo') : i18nT('utils.pantallaDelPin.cifra', 'Cifra {{d}}', { d })}
      style={{ height: 68, borderRadius: 16, cursor: 'pointer', touchAction: 'manipulation',
        border: '1px solid ' + (rojo ? 'rgba(220,38,38,0.35)' : 'var(--border)'),
        background: rojo ? 'rgba(220,38,38,0.10)' : 'var(--surface)', color: rojo ? '#dc2626' : 'var(--text-primary, var(--text))',
        boxShadow: '0 2px 6px rgba(16,24,40,0.06)', fontSize: 27, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
      {children || d}
    </button>
  )
}

export function PuntosPin({ n, largo = LARGO_PIN, error, temblor }) {
  const color = error ? '#ef4444' : 'var(--text-primary, var(--text))'
  const puntos = []
  for (let i = 0; i < largo; i++) {
    const puesto = i < n
    puntos.push(
      <span key={i} data-punto-pin={puesto ? 'puesto' : 'vacio'} style={{ width: 18, height: 18, borderRadius: '50%',
        border: '2.5px solid ' + (puesto ? color : 'var(--border-fuerte, var(--text-muted))'),
        background: puesto ? color : 'transparent', transform: puesto ? 'scale(1.12)' : 'scale(1)',
        transition: 'background .12s, transform .12s, border-color .12s' }} />
    )
  }
  return (
    <div data-puntos-pin={n} aria-label={i18nT('utils.pantallaDelPin.de_cifras', '{{n}} de {{largo}} cifras', { n, largo })}
      style={{ display: 'flex', justifyContent: 'center', gap: 13, margin: '6px 0 4px', animation: temblor ? 'pinTiembla .4s' : 'none' }}>
      {puntos}
    </div>
  )
}

const ICONO_BORRAR = <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" /><line x1="18" y1="9" x2="12" y2="15" /><line x1="12" y1="9" x2="18" y2="15" /></svg>

export function TecladoPin({ onPulsar, disabled }) {
  return (
    <div data-teclado-pin="" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: 340, margin: '6px auto 0' }}>
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <TeclaPin key={d} d={d} onPulsar={onPulsar} disabled={disabled} />)}
      <TeclaPin d="C" tono="rojo" onPulsar={onPulsar} disabled={disabled}><span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.5px' }}>{i18nT('utils.pantallaDelPin.borrar', 'BORRAR')}</span></TeclaPin>
      <TeclaPin d="0" onPulsar={onPulsar} disabled={disabled} />
      <TeclaPin d="<" onPulsar={onPulsar} disabled={disabled}>{ICONO_BORRAR}</TeclaPin>
    </div>
  )
}

export function CaraPersona({ p, grande, onElegir, disabled }) {
  const tam = grande ? 72 : 54
  const cara = p && p.avatar
    ? <img src={p.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    : <span style={{ fontSize: grande ? 26 : 19, fontWeight: 800, color: '#fff' }}>{(p && p.iniciales) || '?'}</span>
  const circulo = (
    <span style={{ width: tam, height: tam, borderRadius: '50%', overflow: 'hidden', background: 'var(--primary)', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(16,24,40,0.12)' }}>{cara}</span>
  )
  if (!onElegir) return circulo
  return (
    <button type="button" data-persona-pin={p.id} onClick={() => onElegir(p)} disabled={disabled}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, padding: '12px 6px', borderRadius: 14,
        border: '1px solid ' + (p.yo ? 'var(--primary)' : 'var(--border)'), background: 'var(--surface)', cursor: 'pointer',
        color: 'var(--text-primary, var(--text))', minWidth: 0 }}>
      {circulo}
      <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', maxWidth: '100%' }}>{tDato(p.nombre)}</span>
      {p.yo && <span style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>{i18nT('utils.pantallaDelPin.tu_sesion', 'Tu sesión')}</span>}
      {p.pin === false && <span data-sin-pin="" style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, lineHeight: 1.3 }}>{i18nT('utils.pantallaDelPin.sin_pin_entra_con_tu', 'Sin PIN: entra con tu contraseña')}</span>}
    </button>
  )
}

const enlace = { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: '6px 4px' }
const botonSuave = { width: '100%', padding: '11px 14px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)',
  color: 'var(--text-primary, var(--text))', fontSize: 14, fontWeight: 700, cursor: 'pointer' }
const botonFuerte = { ...botonSuave, border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontWeight: 800 }

// Lo que dice el servidor cuando algo no va, con sus palabras; si no hay respuesta, que no hay conexión.
function mensajeDe(e, porDefecto) {
  if (!e || !e.response) return marca('utils.pantallaDelPin.no_hay_conexion_con_el_2', 'No hay conexión con el servidor. Espera unos segundos y vuelve a intentarlo.')
  if (e.response.status === 401) return marca('utils.pantallaDelPin.tu_sesion_ha_caducado_te', 'Tu sesión ha caducado. Te llevo a la pantalla de entrada…')
  return (e.response.data && e.response.data.error) || tDato(porDefecto)
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  props:
//   api            el cliente (services/api).
//   usuario        { id, nombre, apellidos } de la sesión.
//   modo           'desbloquear' | 'elegir' | 'crear' (el de arriba al abrir).
//   cambio         (modo desbloquear) se ha abierto con «Cambiar de usuario» o es un puesto compartido: «Escribe tu PIN».
//   puedeCambiar   ¿puede entrar aquí OTRA persona con su PIN? (lo dice el servidor: pin_unico.cambio_usuario)
//   avisoCambio    por qué no sale, si no sale (fuera de la red de la clínica…).
//   pedirContrasena (modo crear) ¿hay que escribir la contraseña para ponerse el PIN?
//   extras         botones de huella / Face ID que pinta quien monta esto (ya decididos por la regla).
//   onAbierto()    la sesión de antes sigue: quitar el candado.
//   onSesionNueva(datos) {token, agente} de otra persona: entrar con ella y recargar.
//   yoTengoPin     ¿la persona de la sesión tiene PIN propio? («Seguir como …» pide su PIN; si no, su contraseña.)
//   contrasenaInicial (modo crear) la contraseña que se acaba de escribir al desbloquear (no se vuelve a pedir).
//   campoUsuario   (modo crear) la casilla oculta del usuario de la sesión (utils/reautenticar · usuarioDeLaSesion): con
//                  ella el llavero del iPhone sabe qué contraseña ofrecer.
//   onContrasena() «Entrar con mi contraseña»: la tarjeta de la contraseña (SIEMPRE que se pase).
//   onSalir()      «Cerrar sesión» (SIEMPRE que se pase).   onCreado()  PIN puesto.
//   onOtraCuenta() «Entrar con otra cuenta»: al inicio de sesión de siempre (en «¿Quién eres?»).
//   onAhoraNo()    (modo crear) «Ahora no»: nunca se obliga a crearlo (t217: tampoco en un puesto compartido).
// ══════════════════════════════════════════════════════════════════════════════════════════════
export function PantallaDelPin({ api, usuario, modo: modoInicial = 'desbloquear', cambio = false, puedeCambiar = false, avisoCambio = null,
  pedirContrasena = true, extras = null, yoTengoPin = true, contrasenaInicial = '', campoUsuario = null, onAbierto, onSesionNueva, onContrasena, onSalir, onCreado, onOtraCuenta, onAhoraNo }) {
  const [modo, setModo] = useState(modoInicial)
  const [pin, setPin] = useState('')
  // modo crear (t217): el PIN nuevo y su repetición, en sus casillas; nada se guarda hasta pulsar «Guardar mi PIN».
  const [nuevoPin, setNuevoPin] = useState('')
  const [repetidoPin, setRepetidoPin] = useState('')
  const [contrasena, setContrasena] = useState(contrasenaInicial || '')   // la recién escrita al desbloquear, si la hay
  const [error, setError] = useState('')
  const [temblor, setTemblor] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [personas, setPersonas] = useState(null)    // null = sin pedir todavía
  const [aviso, setAviso] = useState(avisoCambio)
  const [elegida, setElegida] = useState(null)
  const [clave, setClave] = useState('')            // modo clave: la contraseña de quien no tiene PIN
  const [reloj, setReloj] = useState(() => new Date())
  const pinRef = useRef('')
  const temblorRef = useRef(null)
  const ocupadoRef = useRef(false)
  // La casilla de la contraseña (modo crear): con el llavero del iPhone el valor está en la casilla aunque el estado de
  // React siga vacío (utils/reautenticar cuenta por qué). Se lee de aquí si el estado no lo tiene.
  const contrasenaRef = useRef(null)
  const claveRef = useRef(null)

  useEffect(() => { setModo(modoInicial) }, [modoInicial])
  useEffect(() => { const iv = setInterval(() => setReloj(new Date()), 15000); return () => clearInterval(iv) }, [])
  useEffect(() => () => { if (temblorRef.current) clearTimeout(temblorRef.current) }, [])

  const limpiarPin = useCallback(() => { pinRef.current = ''; setPin('') }, [])
  const fallo = useCallback((texto) => {
    setError(texto); setTemblor(true); limpiarPin()
    if (temblorRef.current) clearTimeout(temblorRef.current)
    temblorRef.current = setTimeout(() => setTemblor(false), 450)
  }, [limpiarPin])

  // ── LAS CARAS: se piden al entrar en «¿Quién eres?» ───────────────────────────────────────
  useEffect(() => {
    if (modo !== 'elegir' || personas !== null) return undefined
    let vivo = true
    api.get('/seguridad/cambio-usuario/personas')
      .then((r) => {
        if (!vivo) return
        const d = (r && r.data) || {}
        setPersonas(Array.isArray(d.personas) ? d.personas : [])
        if (d.disponible === false && d.aviso) setAviso(d.aviso)
      })
      .catch((e) => { if (vivo) { setPersonas([]); setAviso(tDato(mensajeDe(e, marca('utils.pantallaDelPin.no_se_ha_podido_cargar', 'No se ha podido cargar quién puede entrar aquí.')))) } })
    return () => { vivo = false }
  }, [modo, personas, api])

  // ── ENVIAR EL PIN (desbloquear) ───────────────────────────────────────────────────────────────
  // t217 · SOLO para desbloquear: ponerse el PIN ya no pasa por aquí (ni por el teclado), sino por `guardarNuevo`, abajo.
  const enviar = useCallback(async (valor) => {
    if (ocupadoRef.current || modo !== 'desbloquear') return
    ocupadoRef.current = true; setOcupado(true); setError('')
    try {
      // t136 · EL PIN RECONOCE A LA PERSONA: la de la sesión sigue; otra de la clínica entra con su sesión nueva.
      let d
      try {
        const r = await api.post('/seguridad/pin/entrar', { pin: valor })
        d = (r && r.data) || {}
      } catch (e) {
        // Un servidor de antes (sin /pin/entrar): como hasta hoy, solo la persona de la sesión.
        if (!(e && e.response && e.response.status === 404)) throw e
        const r = await api.post('/seguridad/pin/desbloquear', { pin: valor })
        d = { ...((r && r.data) || {}), mismo: true }
      }
      limpiarPin()
      if (d.token && d.agente) { onSesionNueva && onSesionNueva(d); return }
      onAbierto && onAbierto(d)
    } catch (e) {
      if (e && e.response && e.response.status === 429) fallo(mensajeDe(e, marca('utils.pantallaDelPin.demasiados_intentos', 'Demasiados intentos.')))
      else fallo(mensajeDe(e, 'PIN incorrecto'))
    } finally { ocupadoRef.current = false; setOcupado(false) }
  }, [modo, api, onAbierto, onSesionNueva, fallo, limpiarPin])

  // ── PONERSE EL PIN (modo crear) · t217 (13-oct-2026, B-1637) ─────────────────────────────────────
  //  UN PIN NUNCA SE PONE SOLO: con sus dos casillas y el botón «Guardar mi PIN», nunca al teclear. Y con la contraseña:
  //  la recién escrita al desbloquear (`contrasenaInicial`) o la de su casilla (el servidor la pide siempre la primera vez).
  const guardarNuevo = useCallback(async () => {
    if (ocupadoRef.current) return
    const a = String(nuevoPin || '')
    const b = String(repetidoPin || '')
    const pass = contrasena || (contrasenaRef.current && typeof contrasenaRef.current.value === 'string' ? contrasenaRef.current.value : '')
    if (!/^\d{6}$/.test(a)) { setError(i18nT('utils.pantallaDelPin.tu_pin_tiene_que_tener', 'Tu PIN tiene que tener 6 cifras.')); return }
    if (a !== b) { setRepetidoPin(''); setError(i18nT('utils.pantallaDelPin.los_dos_pin_no_coinciden', 'Los dos PIN no coinciden. Escríbelo otra vez.')); return }
    if (!pass) { setError(i18nT('utils.pantallaDelPin.escribe_tu_contrasena_para_crear', 'Escribe tu contraseña para crear tu PIN.')); return }
    ocupadoRef.current = true; setOcupado(true); setError('')
    try {
      await api.put('/seguridad/pin', { pin: a, password: pass })
      setNuevoPin(''); setRepetidoPin(''); setContrasena('')
      onCreado && onCreado()
    } catch (e) {
      setRepetidoPin('')
      setError(tDato(mensajeDe(e, marca('utils.pantallaDelPin.no_se_ha_podido_guardar', 'No se ha podido guardar el PIN.'))))
    } finally { ocupadoRef.current = false; setOcupado(false) }
  }, [nuevoPin, repetidoPin, contrasena, api, onCreado])

  // ── ENTRAR CON LA CONTRASEÑA QUIEN NO TIENE PIN ─────────────────────────────────────────────
  //  Por el inicio de sesión de siempre (su freno de intentos, su sesión nueva), en ESTA clínica. Con la sesión nueva en la
  //  mano, la de antes se cierra con su propia llave (la que sigue guardada) y se entra como con el PIN.
  const entrarConClave = useCallback(async (escrita) => {
    if (ocupadoRef.current || !elegida) return
    const pass = escrita || clave
    if (!pass) { setError(i18nT('utils.pantallaDelPin.escribe_tu_contrasena', 'Escribe tu contraseña.')); return }
    ocupadoRef.current = true; setOcupado(true); setError('')
    try {
      const cid = usuario && usuario.clinica_id
      const r = await api.post('/auth/login', { email: elegida.email, password: pass, ...(cid ? { clinica_id: cid } : {}) })
      const d = (r && r.data) || {}
      if (!(d.token && d.agente)) { setError(i18nT('utils.pantallaDelPin.tu_cuenta_pide_un_paso', 'Tu cuenta pide un paso más para entrar: pulsa «Entrar con otra cuenta».')); return }
      // t136d · con la sesión NUEVA como prueba de que esta persona acaba de entrar (sin ella, el servidor no cierra ni apunta nada).
      try { await api.post('/seguridad/cambio-usuario/cerrar-anterior', { entra: d.agente.id, token: d.token }) } catch (_) { /* si no se puede, la de antes caduca sola */ }
      setClave('')
      onSesionNueva && onSesionNueva(d)
    } catch (e) {
      const st = e && e.response && e.response.status
      if (st === 300) setError(i18nT('utils.pantallaDelPin.tu_cuenta_pide_elegir_la', 'Tu cuenta pide elegir la clínica: pulsa «Entrar con otra cuenta».'))
      // El 401 del inicio de sesión es «esa contraseña no es», no «tu sesión ha caducado».
      else if (st === 401 || st === 400 || st === 403) setError((e.response.data && e.response.data.error) || i18nT('utils.pantallaDelPin.contrasena_incorrecta', 'Contraseña incorrecta'))
      else setError(tDato(mensajeDe(e, marca('utils.pantallaDelPin.no_se_ha_podido_entrar_2', 'No se ha podido entrar.'))))
    } finally { ocupadoRef.current = false; setOcupado(false) }
  }, [elegida, clave, usuario, api, onSesionNueva])

  const pulsar = useCallback((d) => {
    if (ocupadoRef.current) return
    let v = pinRef.current
    if (d === 'C') v = ''
    else if (d === '<') v = v.slice(0, -1)
    else if (/^\d$/.test(d) && v.length < LARGO_PIN) v = v + d
    else return
    if (v.length > 0) setError('')
    pinRef.current = v; setPin(v)
    if (v.length === LARGO_PIN) enviar(v)
  }, [enviar])

  // EL TECLADO DEL ORDENADOR TAMBIÉN VALE: en recepción se escribe con el teclado, no tocando la pantalla.
  // t217 · SOLO PARA DESBLOQUEAR. En modo crear no se escucha: lo que se teclea va a sus casillas (B-1637).
  useEffect(() => {
    if (modo !== 'desbloquear' || typeof window === 'undefined' || !window.addEventListener) return undefined
    const tecla = (ev) => {
      const t = ev && ev.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return
      if (/^[0-9]$/.test(ev.key)) { pulsar(ev.key); ev.preventDefault && ev.preventDefault() }
      else if (ev.key === 'Backspace') { pulsar('<'); ev.preventDefault && ev.preventDefault() }
      else if (ev.key === 'Escape') pulsar('C')
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [modo, pulsar])

  // t136 · la lista es SOLO de quien todavía no tiene PIN (con su contraseña): quien lo tiene lo escribe en el teclado.
  const elegir = (p) => {
    setError(''); limpiarPin()
    if (!p || p.pin !== false) return
    // La de la sesión de ahora, la tarjeta de siempre; otra persona, su inicio de sesión.
    if (p.yo) { if (onContrasena) onContrasena(); return }
    setElegida(p); setClave(''); setModo('clave')
  }
  const volver = () => { setError(''); limpiarPin(); setElegida(null); setNuevoPin(''); setRepetidoPin(''); setClave(''); setModo(modoInicial === 'elegir' ? 'elegir' : 'desbloquear') }
  const irAElegir = () => { setError(''); limpiarPin(); setElegida(null); setModo('elegir') }
  const sinPin = (personas || []).filter((p) => p && p.pin === false && !p.yo)

  const nombre = [usuario && usuario.nombre, usuario && usuario.apellidos].filter(Boolean).join(' ') || marca('utils.pantallaDelPin.tu_sesion', 'Tu sesión')
  const yo = { id: usuario && usuario.id, nombre, iniciales: ((String((usuario && usuario.nombre) || '').trim()[0] || '') + (String((usuario && usuario.apellidos) || '').trim()[0] || '')).toUpperCase() || '?', avatar: null }
  const hora = reloj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  const fecha = reloj.toLocaleDateString(localeFechas(), { weekday: 'long', day: 'numeric', month: 'long' })

  let cuerpo
  if (modo === 'elegir') {
    cuerpo = (
      <>
        <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 4 }}>{i18nT('utils.pantallaDelPin.sin_pin_todavia', 'Sin PIN todavía')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>{i18nT('utils.pantallaDelPin.toca_tu_nombre_y_entra', 'Toca tu nombre y entra con tu contraseña. Si ya tienes PIN, vuelve y escríbelo en el teclado.')}</div>
        {personas === null && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 18 }}>{i18nT('utils.pantallaDelPin.un_momento', 'Un momento…')}</div>}
        {personas !== null && sinPin.length > 0 && (
          <div data-caras-pin="" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 10, maxHeight: 'calc(46vh / var(--z, 1))', overflowY: 'auto', padding: 2 }}>
            {sinPin.map((p) => <CaraPersona key={p.id} p={p} onElegir={elegir} disabled={ocupado} />)}
          </div>
        )}
        {personas !== null && sinPin.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '10px 4px', lineHeight: 1.5 }}>
            {aviso || i18nT('utils.pantallaDelPin.aqui_todo_el_equipo_tiene', 'Aquí todo el equipo tiene ya su PIN: escríbelo en el teclado. Si no lo recuerdas, «Entrar con otra cuenta».')}
          </div>
        )}
        {personas !== null && sinPin.length > 0 && aviso && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10 }}>{aviso}</div>}
      </>
    )
  } else if (modo === 'clave' && elegida) {
    cuerpo = (
      <>
        <CaraPersona p={elegida} grande />
        <div style={{ fontSize: 19, fontWeight: 800, marginTop: 8 }}>{i18nT('utils.pantallaDelPin.hola', 'Hola, {{nombre}}', { nombre: elegida.nombre })}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>{i18nT('utils.pantallaDelPin.todavia_no_tienes_pin_entra', 'Todavía no tienes PIN: entra con tu contraseña.')}</div>
        <form data-form-clave-otro="" onSubmit={(e) => { e.preventDefault(); entrarConClave(claveRef.current && typeof claveRef.current.value === 'string' ? claveRef.current.value : '') }}
          style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
          <input type="email" name="username" autoComplete="username" value={elegida.email || ''} readOnly data-correo-otro=""
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14,
              background: 'var(--surface-2)', color: 'var(--text-secondary)' }} />
          <input type="password" name="password" autoComplete="current-password" value={clave} onChange={(e) => setClave(e.target.value)}
            placeholder={i18nT('utils.pantallaDelPin.tu_contrasena', 'Tu contraseña')} ref={claveRef} data-clave-otro="" autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14,
              background: 'var(--surface)', color: 'var(--text-primary, var(--text))' }} />
          <button type="submit" data-entrar-clave-otro="" disabled={ocupado} style={botonFuerte}>{ocupado ? i18nT('utils.pantallaDelPin.entrando', 'Entrando…') : i18nT('utils.pantallaDelPin.entrar', 'Entrar')}</button>
        </form>
      </>
    )
  } else if (modo === 'crear') {
    // t217 · B-1637 · NO SE PARECE A LA DE DESBLOQUEAR: «Crea tu PIN», sus casillas y un botón. Nada se guarda al teclear. La
    // contraseña, salvo que se acabe de escribir para desbloquear (el servidor la pide siempre la primera vez).
    const conClave = pedirContrasena || !contrasenaInicial
    const casilla = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10,
      fontSize: 14, background: 'var(--surface)', color: 'var(--text-primary, var(--text))' }
    const casillaPin = { ...casilla, flex: '1 1 0', minWidth: 0, textAlign: 'center', letterSpacing: 4, fontWeight: 800, fontSize: 17 }
    const seis = (v) => String(v || '').replace(/\D/g, '').slice(0, LARGO_PIN)
    cuerpo = (
      <>
        <div data-crear-pin-titulo="" style={{ fontSize: 19, fontWeight: 800, marginBottom: 4 }}>{i18nT('utils.pantallaDelPin.crea_tu_pin', 'Crea tu PIN')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
          {tx('utils.pantallaDelPin.vas_a_crear_tu_pin', 'Vas a crear tu PIN de 6 cifras: con él fichas, desbloqueas la aplicación y entras en cualquier ordenador de la clínica. Escríbelo dos veces y pulsa «Guardar mi PIN». Apréndetelo: no lo ve nadie, tampoco administración. Ni todas las cifras iguales ni seguidas.{{x}}', { x: onAhoraNo ? i18nT('utils.pantallaDelPin.si_ahora_no_quieres_pulsa', ' Si ahora no quieres, pulsa «Ahora no»: puedes crearlo cuando quieras en Mi perfil.') : '' })}
        </div>
        <form data-form-crear-pin="" onSubmit={(e) => { e.preventDefault(); guardarNuevo() }}
          style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
          {campoUsuario && <input {...campoUsuario} />}
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="password" inputMode="numeric" autoComplete="off" name="pin-nuevo" maxLength={LARGO_PIN} value={nuevoPin}
              onChange={(e) => { setNuevoPin(seis(e.target.value)); setError('') }} placeholder={i18nT('utils.pantallaDelPin.pin_nuevo', 'PIN nuevo')} aria-label={i18nT('utils.pantallaDelPin.tu_pin_nuevo', 'Tu PIN nuevo')}
              data-pin-nuevo="" data-lpignore="true" data-1p-ignore="" style={casillaPin} />
            <input type="password" inputMode="numeric" autoComplete="off" name="pin-repetido" maxLength={LARGO_PIN} value={repetidoPin}
              onChange={(e) => { setRepetidoPin(seis(e.target.value)); setError('') }} placeholder={i18nT('utils.pantallaDelPin.repitelo', 'Repítelo')} aria-label={i18nT('utils.pantallaDelPin.repite_tu_pin_nuevo', 'Repite tu PIN nuevo')}
              data-pin-repetido="" data-lpignore="true" data-1p-ignore="" style={casillaPin} />
          </div>
          {conClave && (
            <input type="password" value={contrasena} onChange={(e) => setContrasena(e.target.value)} placeholder={i18nT('utils.pantallaDelPin.tu_contrasena', 'Tu contraseña')}
              name="password" autoComplete="current-password" data-contrasena-pin="" ref={contrasenaRef} style={casilla} />
          )}
          <button type="submit" data-guardar-pin="" disabled={ocupado} style={botonFuerte}>{ocupado ? i18nT('utils.pantallaDelPin.guardando', 'Guardando…') : i18nT('utils.pantallaDelPin.guardar_mi_pin', 'Guardar mi PIN')}</button>
        </form>
      </>
    )
  } else {
    // t136 · EL TECLADO QUE RECONOCE: como el quiosco, sin elegir a nadie antes.
    cuerpo = (
      <>
        {!cambio && <CaraPersona p={yo} grande />}
        <div style={{ fontSize: 19, fontWeight: 800, marginTop: cambio ? 0 : 8 }}>{cambio ? i18nT('utils.pantallaDelPin.escribe_tu_pin', 'Escribe tu PIN') : tDato(yo.nombre)}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
          {cambio ? i18nT('utils.pantallaDelPin.te_reconoce_por_tu_pin', 'Te reconoce por tu PIN: no hace falta buscar tu nombre.') : i18nT('utils.pantallaDelPin.sesion_bloqueada_escribe_tu_pin', 'Sesión bloqueada · escribe tu PIN para seguir')}
        </div>
        {puedeCambiar && (
          <div data-otra-persona-pin="" style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 2, lineHeight: 1.45 }}>
            {cambio ? i18nT('utils.pantallaDelPin.ahora_esta_abierta_la_sesion', 'Ahora está abierta la sesión de {{nombre}}. Con su PIN sigue; con el tuyo, entras tú.', { nombre: tDato(nombre) }) : i18nT('utils.pantallaDelPin.eres_otra_persona_escribe_tu', '¿Eres otra persona? Escribe tu PIN y entrarás con tu sesión.')}
          </div>
        )}
        {!yoTengoPin && !cambio && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 2 }}>{tx('utils.pantallaDelPin.todavia_no_tiene_pin_entra', '{{nombre}} todavía no tiene PIN: entra con tu contraseña (abajo).', { nombre: tDato(nombre) })}</div>
        )}
        <PuntosPin n={pin.length} error={!!error} temblor={temblor} />
      </>
    )
  }

  return (
    <div className="modal-safe" data-pantalla-pin={modo} role="dialog" aria-modal="true" aria-label={i18nT('utils.pantallaDelPin.pantalla_del_pin', 'Pantalla del PIN')}
      style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--surface-2, #f4f6f8)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 22, boxShadow: '0 10px 40px rgba(16,24,40,0.10)', border: '1px solid var(--border)',
        padding: 'clamp(18px, (4vw / var(--z, 1)), 28px)', width: '100%', maxWidth: modo === 'elegir' ? 560 : 440, textAlign: 'center', margin: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 10, marginBottom: 14, color: 'var(--text-secondary)' }}>
          <span style={{ fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary, var(--text))' }}>{hora}</span>
          <span style={{ fontSize: 13 }}>{primeraMayuscula(fecha)}</span>
        </div>
        {cuerpo}
        <div role="alert" style={{ minHeight: 20, fontSize: 13, fontWeight: 700, color: '#ef4444', margin: '4px 0 6px' }}>
          {ocupado ? <span style={{ color: 'var(--text-muted)' }}>{i18nT('utils.pantallaDelPin.comprobando', 'Comprobando…')}</span> : error}
        </div>
        {modo === 'desbloquear' && <TecladoPin onPulsar={pulsar} disabled={ocupado} />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
          {modo === 'desbloquear' && !cambio && extras}
          {/* t136 · quien tiene PIN no se busca en ninguna lista: lo escribe arriba. La lista es para quien aún no lo tiene. */}
          {modo === 'desbloquear' && puedeCambiar && (
            <button type="button" data-sin-pin-lista="" onClick={irAElegir} disabled={ocupado} style={botonSuave}>
              {i18nT('utils.pantallaDelPin.todavia_no_tienes_pin_entra_2', '¿Todavía no tienes PIN? Entra con tu contraseña')}
            </button>
          )}
          {modo === 'crear' && onAhoraNo && (
            <button type="button" data-ahora-no="" onClick={onAhoraNo} disabled={ocupado} style={botonSuave}>{i18nT('utils.pantallaDelPin.ahora_no', 'Ahora no')}</button>
          )}
          {(modo === 'elegir' || (modo === 'desbloquear' && cambio)) && onOtraCuenta && (
            <button type="button" data-otra-cuenta="" onClick={onOtraCuenta} disabled={ocupado} style={botonSuave}>{i18nT('utils.pantallaDelPin.entrar_con_otra_cuenta', 'Entrar con otra cuenta')}</button>
          )}
          {(modo === 'clave' || (modo === 'elegir' && modoInicial !== 'elegir')) && (
            <button type="button" onClick={volver} disabled={ocupado} style={botonSuave}>{i18nT('utils.pantallaDelPin.volver', 'Volver')}</button>
          )}
          {modo === 'desbloquear' && !puedeCambiar && aviso && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>{aviso}</div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
          {modo !== 'crear' && modo !== 'clave' && onContrasena && (
            <button type="button" data-usar-contrasena="" onClick={onContrasena} style={enlace}>{i18nT('utils.pantallaDelPin.entrar_con_mi_contrasena', 'Entrar con mi contraseña')}</button>
          )}
          {onSalir && <button type="button" data-salir-pin="" onClick={onSalir} style={enlace}>{i18nT('utils.pantallaDelPin.cerrar_sesion', 'Cerrar sesión')}</button>}
        </div>
      </div>
      <style>{`
        @keyframes pinTiembla{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}
        .pin-tecla{transition:transform .06s ease, background .06s ease, box-shadow .06s ease}
        .pin-tecla:active{transform:scale(.94);background:var(--primary);color:var(--sobre-primary);box-shadow:0 0 0 4px color-mix(in srgb, var(--primary) 30%, transparent)}
        .pin-tecla:active svg{stroke:#fff}
        .pin-tecla:active span{color:#fff !important}
        @media (prefers-reduced-motion: reduce){.pin-tecla{transition:background .06s ease}.pin-tecla:active{transform:none}}
      `}</style>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t95c · LA PANTALLA DE ENTRAR DE UN PUESTO COMPARTIDO (sin sesión). El dueño (5-oct-2026): «en los ordenadores marcados
//  por la clínica como puesto compartido, la pantalla de entrar enseña la lista de personas y se entra con nombre + PIN.
//  Debajo, "Entrar con contraseña" y "Entrar con otra cuenta"». La monta pages/Login cuando el servidor dice que ESTE
//  ordenador lo es (GET /api/puesto/personas con su llave; routes/puestoCompartido.js tiene todas las puertas).
//   props: api, datos { puesto, clinica, personas }, cabeceras (la llave), onEntrar(d) (token + agente: entrar como en
//   el inicio de sesión), onConContrasena(correo|null) (el formulario de siempre, con su correo puesto si se sabe),
//   onOtraCuenta() (el formulario de siempre, vacío).
// ══════════════════════════════════════════════════════════════════════════════════════════════
export function PuestoSinSesion({ api, datos, cabeceras = {}, onEntrar, onConContrasena, onOtraCuenta }) {
  const [elegida, setElegida] = useState(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const pinRef = useRef('')
  const ocupadoRef = useRef(false)
  const personas = (datos && Array.isArray(datos.personas)) ? datos.personas : []

  const enviar = useCallback(async (valor) => {
    if (ocupadoRef.current || !elegida) return
    ocupadoRef.current = true; setOcupado(true); setError('')
    try {
      const r = await api.post('/puesto/entrar', { agente_id: elegida.id, pin: valor }, { headers: cabeceras })
      const d = (r && r.data) || {}
      pinRef.current = ''; setPin('')
      if (d.token && d.agente) { onEntrar && onEntrar(d); return }
      setError(i18nT('utils.pantallaDelPin.no_se_ha_podido_entrar', 'No se ha podido entrar. Entra con tu contraseña.'))
    } catch (e) {
      pinRef.current = ''; setPin('')
      setError((e && e.response && e.response.data && e.response.data.error) || (e && e.response ? i18nT('utils.pantallaDelPin.pin_incorrecto', 'PIN incorrecto') : i18nT('utils.pantallaDelPin.no_hay_conexion_con_el', 'No hay conexión con el servidor.')))
    } finally { ocupadoRef.current = false; setOcupado(false) }
  }, [elegida, api, cabeceras, onEntrar])

  const pulsar = useCallback((d) => {
    if (ocupadoRef.current) return
    let v = pinRef.current
    if (d === 'C') v = ''
    else if (d === '<') v = v.slice(0, -1)
    else if (/^[0-9]$/.test(d) && v.length < LARGO_PIN) v = v + d
    else return
    if (v.length > 0) setError('')
    pinRef.current = v; setPin(v)
    if (v.length === LARGO_PIN) { enviar(v) }
  }, [enviar])

  useEffect(() => {
    if (!elegida || typeof window === 'undefined' || !window.addEventListener) return undefined
    const tecla = (ev) => {
      if (/^[0-9]$/.test(ev.key)) { pulsar(ev.key); ev.preventDefault && ev.preventDefault() }
      else if (ev.key === 'Backspace') { pulsar('<'); ev.preventDefault && ev.preventDefault() }
      else if (ev.key === 'Escape') pulsar('C')
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [elegida, pulsar])

  const elegir = (p) => {
    setError(''); pinRef.current = ''; setPin('')
    if (p && false === p.pin) { if (onConContrasena) onConContrasena(p.email || null); return }
    setElegida(p)
  }

  return (
    <div className="modal-safe" data-puesto-sin-sesion="" style={{ minHeight: 'var(--alto-pantalla)', background: 'var(--surface-2, #f4f6f8)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 22, boxShadow: '0 10px 40px rgba(16,24,40,0.10)', border: '1px solid var(--border)',
        padding: 'clamp(18px, (4vw / var(--z, 1)), 28px)', width: '100%', maxWidth: elegida ? 440 : 560, textAlign: 'center', margin: 'auto' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
          {[datos && datos.clinica, datos && datos.puesto].filter(Boolean).join(' · ')}
        </div>
        {!elegida && (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 4 }}>{i18nT('utils.pantallaDelPin.quien_eres', '¿Quién eres?')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>{i18nT('utils.pantallaDelPin.toca_tu_nombre_y_escribe', 'Toca tu nombre y escribe tu PIN.')}</div>
            {personas.length > 0
              ? (
                <div data-caras-pin="" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 10, maxHeight: 'calc(46vh / var(--z, 1))', overflowY: 'auto', padding: 2 }}>
                  {personas.map((p) => <CaraPersona key={p.id} p={p} onElegir={elegir} disabled={ocupado} />)}
                </div>
              )
              : <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: 10 }}>{i18nT('utils.pantallaDelPin.todavia_no_hay_nadie_que', 'Todavía no hay nadie que pueda entrar aquí con su PIN.')}</div>}
          </>
        )}
        {elegida && (
          <>
            <CaraPersona p={elegida} grande />
            <div style={{ fontSize: 19, fontWeight: 800, marginTop: 8 }}>{i18nT('utils.pantallaDelPin.hola', 'Hola, {{nombre}}', { nombre: elegida.nombre })}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>{i18nT('utils.pantallaDelPin.escribe_tu_pin_para_entrar', 'Escribe tu PIN para entrar')}</div>
            <PuntosPin n={pin.length} error={!!error} />
          </>
        )}
        <div role="alert" style={{ minHeight: 20, fontSize: 13, fontWeight: 700, color: '#ef4444', margin: '4px 0 6px' }}>
          {ocupado ? <span style={{ color: 'var(--text-muted)' }}>{i18nT('utils.pantallaDelPin.comprobando', 'Comprobando…')}</span> : error}
        </div>
        {elegida && <TecladoPin onPulsar={pulsar} disabled={ocupado} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
          {elegida && <button type="button" onClick={() => { setElegida(null); setError(''); pinRef.current = ''; setPin('') }} disabled={ocupado} style={botonSuave}>{i18nT('utils.pantallaDelPin.volver', 'Volver')}</button>}
          <button type="button" data-puesto-contrasena="" onClick={() => onConContrasena && onConContrasena(elegida && elegida.email ? elegida.email : null)} style={botonSuave}>{i18nT('utils.pantallaDelPin.entrar_con_contrasena', 'Entrar con contraseña')}</button>
          <button type="button" data-otra-cuenta="" onClick={() => onOtraCuenta && onOtraCuenta()} style={botonSuave}>{i18nT('utils.pantallaDelPin.entrar_con_otra_cuenta', 'Entrar con otra cuenta')}</button>
        </div>
      </div>
    </div>
  )
}
